import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Grid, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { IsComeOn, IsPaf, themeC } from '../theme/themehelper';

function MobileNavBarOrange({ theme, realMode, agentSyndicateBetting, reportSelection, whichToUnderline }: {
    theme: string, realMode: boolean, agentSyndicateBetting: boolean, whichToUnderline: string
    reportSelection: (type: string, id: string) => void
}) {

    const useVisibility = (ref: React.RefObject<HTMLElement>): boolean => {
        const [isVisible, setIsVisible] = useState<boolean>(true);

        useEffect(() => {
            if (!ref.current) return;

            let oldRef = ref.current

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        setIsVisible(entry.isIntersecting);
                    });
                },
                { threshold: 1.0 } // Check if element is fully visible
            );

            observer.observe(oldRef);

            return () => {
                if (oldRef) observer.unobserve(oldRef);
            };
        }, [ref]);

        return isVisible;
    };

    const { t } = useTranslation();

    const isMobileScreen = useMediaQuery('(max-width: 640px)');

    let gridRef = useRef<HTMLDivElement>(null);
    let firstRef = useRef<HTMLDivElement>(null);
    let scrollContainerRef = useRef<HTMLDivElement>(null);
    let isGridVisible = useVisibility(gridRef);
    let isFirstVisible = useVisibility(firstRef);
    let myBets = t('my_bets');

    // Smooth scroll to selected item
    useEffect(() => {

        if (scrollContainerRef.current) {
            const selectedItem = scrollContainerRef.current.querySelector('[data-selected="true"]');
            if (selectedItem) {
                const container = scrollContainerRef.current;
                const itemRect = selectedItem.getBoundingClientRect();
                const containerRect = container.getBoundingClientRect();

                // Calculate position - offset more to the left (30% from left instead of centered)
                const itemLeft = itemRect.left - containerRect.left + container.scrollLeft;
                const targetScroll = itemLeft - (containerRect.width * 0.3);

                container.scrollTo({
                    left: Math.max(0, targetScroll),
                    behavior: 'smooth'
                });
            }
        }
    }, [whichToUnderline]);

    let gap = "";
    let marginOverMenu = "20px";
    let menuPlacement = "center";

    let marginToLeft = "0px";
    let marginToRight = "0px";

    let textStyle: SxProps<Theme> = {
        fontSize: "12px",
        margin: "5px",
        color: "var(--title-bar-color)",
        fontFamily: theme === themeC.Wood ? 'var(--font-body)' : 'var(--font-title-3)',
    }

    let textStyleSelected: SxProps<Theme> = {
        fontSize: "12px",
        margin: "5px",
        color: "var(--title-bar-color)",
        fontFamily: theme === themeC.Wood ? 'var(--font-body)' : 'var(--font-title-3)',
    }
    let itemContainer: SxProps<Theme> = {
        flexGrow: isMobileScreen ? 0 : 1,
        '& > a': { textDecoration: "none" }
    }

    let gridStyleContent: SxProps<Theme> = {
        flexDirection: "var(--flex-direction)",
        flexWrap: "nowrap",
        overflowX: "scroll",
        scrollbarWidth: "none",
        width: "100%",
        gap: "0px",
        position: "relative",
        padding: "4px",
    }

    let gridStyle: SxProps<Theme> = {
        borderRadius: isGridVisible ? (isFirstVisible ? "24px 24px 24px 24px" : "0px 24px 24px 0px") : (isFirstVisible ? "24px 0px 0px 24px" : "0px 0px 0px 0px"),
        paddingTop: "10px",
        padding: "4px",
        color: "var(--title-bar-color)",
        '& > a': { textDecoration: "none" },
        background: "#DDD",
        flexDirection: "var(--flex-direction)",
        margin: "0px 10px 20px 10px",
        flexWrap: "nowrap",
        overflowX: "scroll",
        scrollbarWidth: "none",
        textWrap: "nowrap",
        maxWidth: "720px",
        width: "100%",
        gap: "0px",
        position: "relative",
    };

    let hoverBackground = "#FFFFFF8A";
    if (IsComeOn(theme) || IsPaf(theme)) {
        hoverBackground = "";
        itemContainer.flexGrow = 0;
        gridStyleContent.width = "auto";
        gridStyle.marginLeft = "0px";
        gridStyle.paddingLeft = "0px";
        gridStyleContent.paddingTop = "0px";
        gridStyleContent.paddingLeft = "12px";
        gridStyleContent.paddingRight = "12px";
        gap = "8px";
        marginOverMenu = "0px";
        menuPlacement = "left";
        myBets = t('my_coupons');
    }

    if (theme === themeC.Hard) {
        hoverBackground = "var(--background1)"
        itemContainer.flexGrow = 0;
        gridStyleContent.width = "auto";
        gridStyle.marginLeft = "0px";
        gridStyle.paddingLeft = "0px";
        gridStyleContent.paddingTop = "0px";
        gridStyleContent.paddingLeft = "12px";
        gridStyleContent.paddingRight = "12px";
        gap = "8px";
        marginOverMenu = "20px";
        if (isMobileScreen) menuPlacement = "left";
    }

    let itemStyle: SxProps<Theme> = {
        borderRadius: "24px",
        flexDirection: "var(--flex-direction)",
        padding: "8px 12px 8px 12px",
        height: "32px",
        minWidth: isMobileScreen ? "64px" : "72px",
        justifyContent: "center",
        flexGrow: 1,
        "&:hover": {
            backgroundColor: hoverBackground
        },
        "&:active": {
            backgroundColor: hoverBackground
        },
    };

    let itemStyleSelected: SxProps<Theme> = {
        borderRadius: "24px",
        flexDirection: "var(--flex-direction)",
        padding: "8px 12px 8px 12px",
        height: "32px",
        minWidth: isMobileScreen ? "64px" : "72px",
        background: "#FFF",
        boxShadow: "0px 1px 8px rgba(0,0,0,0.25)",
        justifyContent: "center"
    };

    let toneOutLeft = 'linear-gradient(270deg, #F5F7FB 0%, rgba(237, 237, 237, 0) 100%)';
    let toneOutRight = 'linear-gradient(90deg, #F5F7FB 0%, rgba(237, 237, 237, 0) 100%)';

    if (theme === themeC.ComeOn) {
        itemStyleSelected.background = '#C0F9C8';
        itemStyle.background = '#292F2A';
        textStyle.color = '#C0F9C8';
        textStyle.fontWeight = '700';
        textStyle.fontSize = '15px';
        textStyleSelected.color = '#171C17';
        textStyleSelected.fontWeight = '700';
        textStyleSelected.fontSize = '15px';
        gridStyle.background = "1A1E1A";
        toneOutLeft = 'linear-gradient(to left,rgb(26, 30, 26), 65%, rgba(26, 30, 26, 0))';
        toneOutRight = 'linear-gradient(to right, rgb(26, 30, 26), 65%, rgba(26, 30, 26, 0))';
    }

    if (theme === themeC.Hajper) {
        itemStyleSelected.background = '#A9F7CA';
        itemStyle.background = '#0D194F';
        textStyle.color = '#A9F7CA';
        textStyle.fontWeight = '700';
        textStyle.fontSize = '15px';
        textStyleSelected.color = '#060C27';
        textStyleSelected.fontWeight = '700';
        textStyleSelected.fontSize = '15px';
        gridStyle.background = "060C27";
        toneOutLeft = 'linear-gradient(to left,rgb(26, 30, 26), 65%, rgba(26, 30, 26, 0))';
        toneOutRight = 'linear-gradient(to right, rgb(26, 30, 26), 65%, rgba(26, 30, 26, 0))';
    }

    if (theme === themeC.Snabbare) {

        itemStyleSelected.background = '#0086FF';
        itemStyle.background = '#F0F8FF';
        textStyle.color = '#060C27';
        textStyle.fontWeight = '600';
        textStyle.fontSize = '15px';
        textStyle.fontStyle = 'italic';
        textStyleSelected.color = '#FFFFFF';
        textStyleSelected.fontWeight = '600';
        textStyleSelected.fontSize = '15px';
        textStyleSelected.fontStyle = 'italic';
        gridStyle.background = "#FFFFFF";
        toneOutLeft = 'linear-gradient(to left,rgb(255, 255, 255), 65%, rgba(255, 255, 255,0))';
        toneOutRight = 'linear-gradient(to right, rgb(255, 255, 255), 65%, rgb(255, 255, 255,0))';
    }

    if (theme === themeC.Reviant) {
        itemStyleSelected.background = '#EAEBEB';
        itemStyleSelected.border = '0.8px solid #B18378';
        itemStyleSelected.borderRadius = '100px';
        itemStyleSelected.boxShadow = 'none';
        itemStyle.background = '#EAEBEB';
        itemStyle.border = '0.8px solid transparent';
        itemStyle.borderRadius = '100px';
        textStyle.color = '#2E3236';
        textStyle.fontWeight = '500';
        textStyle.fontSize = '15px';
        textStyleSelected.color = '#B18378';
        textStyleSelected.fontWeight = '500';
        textStyleSelected.fontSize = '15px';
        gridStyle.background = "transparent";
        toneOutLeft = 'linear-gradient(to left,rgb(234, 235, 235), 65%, rgba(234, 235, 235, 0))';
        toneOutRight = 'linear-gradient(to right, rgb(234, 235, 235), 65%, rgba(234, 235, 235, 0))';
    }

    if (theme === themeC.Lyllo) {

        itemStyleSelected.background = '#F02884';
        itemStyle.background = '#FFFFFF';
        textStyle.color = '#9FA2AB';
        textStyle.fontWeight = '600';
        textStyle.fontSize = '15px';
        textStyleSelected.color = '#FFFFFF';
        textStyleSelected.fontWeight = '600';
        textStyleSelected.fontSize = '15px';
        gridStyle.background = "#E8EAEC";
        toneOutLeft = 'linear-gradient(to left,rgb(255, 255, 255), 65%, rgba(255, 255, 255,0))';
        toneOutRight = 'linear-gradient(to right, rgb(255, 255, 255), 65%, rgb(255, 255, 255,0))';
    }

    if (theme === themeC.Stugan) {

        itemStyleSelected.background = '#0E7C54';
        itemStyle.background = '#F9F7F5';
        textStyle.color = '#333333';
        textStyle.fontWeight = '700';
        textStyle.fontSize = '15px';
        textStyleSelected.color = '#FFFFFF';
        textStyleSelected.fontWeight = '700';
        textStyleSelected.fontSize = '15px';
        toneOutLeft = 'linear-gradient(to left,rgb(255, 255, 255), 65%, rgba(255, 255, 255,0))';
        toneOutRight = 'linear-gradient(to right, rgb(255, 255, 255), 65%, rgb(255, 255, 255,0))';
    }

    if (theme === themeC.Kryss) {
        gridStyle.background = "#F2F5F8";
        itemStyleSelected.background = '#009fe3';
        itemStyleSelected.borderRadius = '12px';
        itemStyle.background = '#FFFFFF';
        itemStyle.borderRadius = '12px';
        textStyle.color = '#333333';
        textStyle.fontWeight = '700';        
        textStyleSelected.color = '#FFFFFF';
        textStyleSelected.fontWeight = '700';        
        toneOutLeft = 'linear-gradient(to left,rgb(242, 245, 248), 65%, rgb(242, 245, 248,0))';
        toneOutRight = 'linear-gradient(to right, rgb(242, 245, 248), 65%, rgb(242, 245, 248,0))';        
        gridStyleContent.paddingBottom = "8px";
        myBets = t('my_bets');
        if (isMobileScreen) menuPlacement = "left";        
        itemStyleSelected.paddingLeft = "4px";
        itemStyleSelected.paddingRight = "4px";
        itemStyle.paddingLeft = "4px";
        itemStyle.paddingRight = "4px";
    }

    if (theme === themeC.Hard) {
        gridStyle.background = "var(--background1)";
        itemStyleSelected.background = "#212121";
        itemStyleSelected.borderRadius = '50px';
        itemStyle.background = "var(--background1)";
        itemStyle.borderRadius = '50px';
        textStyle.color = "var(--text-white)"
        textStyle.fontWeight = '900';
        textStyle.fontSize = '16px';
        textStyleSelected.color = "var(--button-color)"
        textStyleSelected.fontWeight = '900';
        textStyleSelected.fontSize = '16px';
        toneOutLeft = 'linear-gradient(to left,rgb(21, 21, 21), 65%, rgba(21, 21, 21, 0))';
        toneOutRight = 'linear-gradient(to right, rgb(21, 21, 21), 65%, rgba(21, 21, 21, 0))';
        gridStyleContent.paddingBottom = "8px";
    }

    if (theme === themeC.GoGo) {
        gridStyle.background = "var(--background1)";
        itemStyleSelected.background = "var(--button-color)";
        itemStyleSelected.transition = 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)';
        itemStyleSelected["&:hover"] = {
            backgroundColor: "var(--button-hover-color)"
        };
        itemStyleSelected.borderRadius = '50px';
        itemStyle.background = "#FFF";
        itemStyle.borderRadius = '100px';
        itemStyle.transition = 'background-color 300ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)';
        itemStyle["&:hover"] = {
            backgroundColor: "#EDEDED"
        };
        itemStyle["&:active"] = {
            backgroundColor: "#EDEDED"
        };
        textStyle.color = "#000"
        textStyle.fontWeight = '700';
        textStyle.textTransform = 'uppercase';
        textStyleSelected.color = "#000"
        textStyleSelected.fontWeight = '700';
        textStyleSelected.textTransform = 'uppercase';
        toneOutLeft = 'linear-gradient(to left,rgb(0, 0, 0), 65%, rgba(0, 0, 0, 0))';
        toneOutRight = 'linear-gradient(to right, rgb(0, 0, 0), 65%, rgba(0, 0, 0, 0))';
        gridStyleContent.paddingBottom = "8px";
        gap = "8px";
        marginToLeft = whichToUnderline === '6' ? "120px" : "110px";
        marginToRight = whichToUnderline === '6' ? "40px" : "10px";
    }

    if (theme === themeC.Lion || theme === themeC.Wood) {
        marginToLeft = whichToUnderline === '6' ? "60px" : "50px";
        marginToRight = whichToUnderline === '6' ? "20px" : "10px";
    }

    if (theme === themeC.Green) {
        gridStyle.background = "transparent";
        itemStyle.background = '#FFFFFF';
        itemStyle.borderRadius = '32px';
        itemStyle.border = '1px solid #6b6b6b';
        itemStyle.padding = '8px 16px';
        itemStyle.height = '36px';
        itemStyle["&:hover"] = { backgroundColor: '#f5fffa' };
        itemStyleSelected.background = '#F5FFFA';
        itemStyleSelected.borderRadius = '32px';
        itemStyleSelected.border = '1px solid #147B45';
        itemStyleSelected.padding = '8px 16px';
        itemStyleSelected.height = '36px';
        itemStyleSelected.boxShadow = 'none';
        textStyle.color = '#666666';
        textStyle.fontWeight = '500';
        textStyle.fontSize = '12px';
        textStyle.fontFamily = 'Roboto, Helvetica Neue, sans-serif';
        textStyleSelected.color = '#111111';
        textStyleSelected.fontWeight = '500';
        textStyleSelected.fontSize = '12px';
        textStyleSelected.fontFamily = 'Roboto, Helvetica Neue, sans-serif';
        toneOutLeft = 'none';
        toneOutRight = 'none';
        gap = "8px";
        itemContainer.flexGrow = 0;
        gridStyleContent.width = "auto";
        gridStyleContent.paddingLeft = "12px";
        gridStyleContent.paddingRight = "12px";
        gridStyleContent.paddingTop = "0px";
        menuPlacement = isMobileScreen ? "left" : "center";
        marginOverMenu = "20px";
    }

    // Set justifyContent after all theme logic has run
    gridStyleContent.justifyContent = menuPlacement === "center" ? "center" : "flex-start";
    gridStyle.justifyContent = menuPlacement === "center" ? "center" : "flex-start";

    return (
        <>
            <Grid container direction="column" alignItems={menuPlacement} sx={{
                paddingRight: "20px",                
            }}>
                <Grid sx={{ height: marginOverMenu }}></Grid>
                <Grid sx={{
                    ...gridStyle, ...{
                        position: 'relative',  // Ensures that the pseudo-element is positioned relative to the Box
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            width: isGridVisible ? "0px" : "24px",
                            height: '52px',
                            backgroundImage: toneOutLeft,
                            boxSizing: "border-box",
                            display: "block",
                            bottom: 0,
                            right: 0
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            width: isFirstVisible ? "0px" : "24px",
                            height: '52px',
                            backgroundImage: toneOutRight,
                            boxSizing: "border-box",
                            display: "block",
                            bottom: 0,
                            left: 0,
                            zIndex: 10
                        }
                    }
                }} container>
                    
                    <Grid container sx={gridStyleContent} ref={scrollContainerRef}>

                        {(theme === themeC.GoGo || theme === themeC.Lion || theme === themeC.Wood) &&
                            /* Spacer to allow scrolling left to see the first item */
                            <Grid item sx={{ width: isMobileScreen ? marginToLeft : "0px", flexShrink: 0 }}></Grid>
                        }

                        <Grid item ref={firstRef} sx={itemContainer} mr={gap} data-selected={whichToUnderline === '0' ? 'true' : 'false'}>
                            <Link to="/clear">
                                <Grid container sx={whichToUnderline === '0' ? itemStyleSelected : itemStyle} alignItems="center" flexWrap="nowrap" onClick={() => reportSelection("home", "mobile_menu")}>
                                    <Typography sx={whichToUnderline === '0' ? textStyleSelected : textStyle}>Start</Typography>
                                </Grid>
                            </Link>
                        </Grid>

                        <Grid item sx={itemContainer} mr={gap} data-selected={whichToUnderline === '4' ? 'true' : 'false'}>
                            <Link to="/roundhistory">
                                <Grid container sx={whichToUnderline === '4' ? itemStyleSelected : itemStyle} alignItems="center" flexWrap="nowrap" onClick={() => reportSelection("rounds", "mobile_menu")}>
                                    <Typography sx={whichToUnderline === '4' ? textStyleSelected : textStyle}>{t('rounds')}</Typography>
                                </Grid>
                            </Link>
                        </Grid>

                        {realMode &&
                            <Grid item sx={itemContainer} mr={gap} data-selected={whichToUnderline === '3' ? 'true' : 'false'}>
                                <Link to="/mycoupons">
                                    <Grid container sx={whichToUnderline === '3' ? itemStyleSelected : itemStyle} alignItems="center" flexWrap="nowrap" onClick={() => reportSelection("coupons", "mobile_menu")}>
                                        <Typography sx={whichToUnderline === '3' ? textStyleSelected : textStyle}>{myBets}</Typography>
                                    </Grid>
                                </Link>
                            </Grid>
                        }

                        {agentSyndicateBetting &&
                            <Grid item sx={itemContainer} mr={gap} data-selected={whichToUnderline === '2' ? 'true' : 'false'}>
                                <Link to="/playagent">
                                    <Grid container sx={whichToUnderline === '2' ? itemStyleSelected : itemStyle} alignItems="center" flexWrap="nowrap" onClick={() => reportSelection("syndicates", "mobile_menu")}>
                                        <Typography sx={whichToUnderline === '2' ? textStyleSelected : textStyle}>{t('bet_with_syndicates')}</Typography>
                                    </Grid>
                                </Link>
                            </Grid>
                        }

                        <Grid ref={gridRef} item sx={itemContainer} mr={marginToRight} data-selected={whichToUnderline === '6' ? 'true' : 'false'}>
                            <Link to="/help">
                                <Grid container sx={whichToUnderline === '6' ? itemStyleSelected : itemStyle} alignItems="center" flexWrap="nowrap" onClick={() => reportSelection("help", "mobile_menu")}>
                                    <Typography sx={whichToUnderline === '6' ? textStyleSelected : textStyle}>{t('help')}</Typography>
                                </Grid>
                            </Link>
                        </Grid>

                        {(theme === themeC.GoGo || theme === themeC.Lion || theme === themeC.Wood) &&
                            /* Spacer to allow scrolling right to see the last item */
                            <Grid item sx={{ width: isMobileScreen ? marginToRight : "0px", flexShrink: 0 }}></Grid>
                        }

                    </Grid>
                </Grid>
            </Grid>
        </>
    );

}

export default MobileNavBarOrange;